import { toast } from 'sonner';
import { getGameGrid, setGameGrid } from '../../../utils/game/spawnConfig';
import { getCurrentPlaymatInversionRegions, getCurrentPlaymatThemeId } from '../../PlaymatPicker';

// Dev-only board editors (grid, UI inversion regions, playmat component
// placements, spawn points), extracted from GameBoard.jsx. These drive
// the editor instances created in the GameBoard constructor and copy
// their output as paste-ready JS for the corresponding config files.
// The editors are mutually exclusive so the top-left popover area has
// a single owner.
//
// Host contract: state (isEditingGrid, isEditingUiInversion,
// isEditingComponents, isPlacingSpawns, spawnConfig), setState,
// forceUpdate, gridEditor, uiInversionEditor, playmatComponentEditor,
// scene, spawnMarkers, updateSpawnMarkers, clearTrackerPreviews,
// hideTrackerCursorPreview.

export function toggleGridEditor(host) {
  host.setState((state) => {
    const next = !state.isEditingGrid;
    if (next) {
      host.gridEditor?.start();
    } else {
      host.gridEditor?.stop();
    }
    return { isEditingGrid: next };
  });
}

export function clearGrid(host) {
  host.gridEditor?.clear();
  const newConfig = { ...host.state.spawnConfig };
  setGameGrid(newConfig, null);
  host.setState({ spawnConfig: newConfig });
}

export function copyGridJSON(host) {
  const grid = getGameGrid(host.state.spawnConfig);
  if (!grid) {
    toast.error('No grid to copy. Draw one first.');
    return;
  }
  const json = JSON.stringify(grid);
  try {
    navigator.clipboard.writeText(json);
    toast.success('Grid JSON copied to clipboard');
  } catch {
    toast.error('Clipboard unavailable. See console.');
  }
  console.log('[GameBoard] gameGrid =', json);
}

export function toggleUiInversionEditor(host) {
  host.setState((state) => {
    const next = !state.isEditingUiInversion;
    if (next) {
      // Mutually exclusive with the grid editor so both popovers
      // don't fight for the top-left corner.
      if (state.isEditingGrid) {
        host.gridEditor?.stop();
      }
      host.uiInversionEditor?.start();
      return { isEditingUiInversion: true, isEditingGrid: false };
    }
    host.uiInversionEditor?.stop();
    // Restore the shader to whatever the saved theme has authored,
    // so closing the editor reverts the live preview to the
    // committed state instead of leaving the in-flight regions in
    // place after a non-copy exit.
    host.scene?.setUiInvertRegions?.(getCurrentPlaymatInversionRegions());
    return { isEditingUiInversion: false };
  });
}

export function addUiInversionRegion(host) {
  host.uiInversionEditor?.addRegion();
  // Force a re-render of the popover so the instruction text
  // updates ("click and drag to draw a new region").
  host.forceUpdate();
}

export function deleteActiveUiInversionRegion(host) {
  host.uiInversionEditor?.deleteActive();
}

export function toggleComponentEditor(host) {
  host.setState((state) => {
    const next = !state.isEditingComponents;
    if (next) {
      // Mutually exclusive with other editors so the top-left
      // popover area has a single owner.
      if (state.isEditingGrid) host.gridEditor?.stop();
      if (state.isEditingUiInversion) host.uiInversionEditor?.stop();
      host.playmatComponentEditor?.start();
      // Strip baked components from the UI texture. The
      // draggable meshes replace them visually while editing.
      host.scene?.setUiSkipComponents?.(true);
      return { isEditingComponents: true, isEditingGrid: false, isEditingUiInversion: false };
    }
    host.playmatComponentEditor?.stop();
    // Restore the texture with components baked in.
    host.scene?.setUiSkipComponents?.(false);
    return { isEditingComponents: false };
  });
}

export function copyComponentPlacementsJSON(host) {
  const placements = host.playmatComponentEditor?.getPlacements() || {};
  const entries = Object.entries(placements);
  if (entries.length === 0) {
    toast.error('No placements to copy.');
    return;
  }
  // Pretty-print as a JS object literal so it pastes straight
  // into COMPONENT_PLACEMENTS in uiOverlayGenerator.js.
  const lines = entries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `  ${k}: { x: ${v.x.toFixed(2)}, z: ${v.z.toFixed(2)} },`);
  const json = `COMPONENT_PLACEMENTS = {\n${lines.join('\n')}\n};`;
  try {
    navigator.clipboard.writeText(json);
    toast.success(`Copied ${entries.length} placements. Paste into uiOverlayGenerator.js`);
  } catch {
    toast.error('Clipboard unavailable. See console.');
  }
  console.log('[GameBoard] COMPONENT_PLACEMENTS =', JSON.stringify(placements, null, 2));
  console.log(json);
}

export function copyUiInversionJSON(host) {
  const regions = host.uiInversionEditor?.getRegions() || [];
  const themeId = getCurrentPlaymatThemeId();
  if (regions.length === 0) {
    toast.error('No regions to copy. Draw one first.');
    return;
  }
  // Pretty-print so it's pasteable straight into the PLAYMAT_THEMES
  // entry without manual reformatting.
  const pretty = regions
    .map((r) => `  { x: ${r.x.toFixed(3)}, y: ${r.y.toFixed(3)}, width: ${r.width.toFixed(3)}, height: ${r.height.toFixed(3)} },`)
    .join('\n');
  const json = `uiInversionRegions: [\n${pretty}\n]`;
  try {
    navigator.clipboard.writeText(json);
    toast.success(`Copied ${regions.length} region${regions.length === 1 ? '' : 's'}. Paste into PLAYMAT_THEMES "${themeId}"`);
  } catch {
    toast.error('Clipboard unavailable. See console.');
  }
  console.log(`[GameBoard] uiInversionRegions for ${themeId} =`, JSON.stringify(regions));
  console.log(json);
}

export function toggleSpawnEditor(host) {
  host.setState((state) => {
    const next = !state.isPlacingSpawns;
    if (next) {
      host.updateSpawnMarkers(state.spawnConfig);
    } else {
      for (const [, mesh] of host.spawnMarkers) {
        host.scene.scene.remove(mesh);
        mesh.geometry.dispose();
        mesh.material.dispose();
      }
      host.spawnMarkers.clear();
      host.clearTrackerPreviews();
      host.hideTrackerCursorPreview();
    }
    return { isPlacingSpawns: next, activeSpawnKey: null, trackerEditing: null };
  });
}
